import { extractSessionEndReason } from './sessionEndReason';
import { HTTP_TOO_MANY_REQUESTS, isTransientStatus, readRetryAfterMs } from './httpFailure';

export const RefreshStatuses = {
  refreshed: 'refreshed',
  sessionEnded: 'sessionEnded',
  unavailable: 'unavailable',
} as const;

/**
 * Result of a `POST /auth/refresh`, reduced to what the caller must do about it.
 *
 * Only `sessionEnded` means the server decided the session is over. `unavailable` (429, no
 * response, timeout, 408, 5xx) says nothing about the session: the refresh cookie is still valid,
 * so logging the user out for it would destroy a good session (AB#2112).
 *
 * `rateLimited` and `retryAfterMs` are set only by the classifier, so `tryRefresh` can retry a
 * 429 once; it strips them before returning.
 */
export type RefreshOutcome =
  | { status: typeof RefreshStatuses.refreshed; token: string }
  | { status: typeof RefreshStatuses.sessionEnded; reasonKey: string | null }
  | { status: typeof RefreshStatuses.unavailable; rateLimited?: true; retryAfterMs?: number };

export function classifyRefreshFailure(error: unknown): Exclude<RefreshOutcome, { status: 'refreshed' }> {
  const response = (error as { response?: { status?: number; headers?: unknown } } | null)?.response;
  const status = response?.status;
  if (status === undefined) return { status: RefreshStatuses.unavailable };
  if (status === HTTP_TOO_MANY_REQUESTS) {
    return { status: RefreshStatuses.unavailable, rateLimited: true, retryAfterMs: readRetryAfterMs(response?.headers) };
  }
  if (isTransientStatus(status)) return { status: RefreshStatuses.unavailable };
  // 401 and every other 4xx: the server rejected the credential itself, today's behaviour.
  return { status: RefreshStatuses.sessionEnded, reasonKey: extractSessionEndReason(error) };
}

/**
 * Rejection for a request whose 401 could not be recovered because the refresh endpoint was
 * unavailable. Distinct from an Axios error on purpose: it carries no HTTP status, so the query
 * layer must recognise it by type and neither retry it (every retry would trigger a new refresh)
 * nor report it (the session is intact and the interceptor already owns the single toast).
 *
 * Lives here rather than in `api.ts` so the query layer can recognise it without importing the
 * axios client, which many tests replace with a partial mock.
 */
export class RefreshUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RefreshUnavailableError';
  }
}

const sessionRefreshedListeners = new Set<() => void>();

/**
 * Called after any successful refresh that follows a shown refresh-unavailable toast (through the
 * toast action or an ordinary 401), so the query layer can re-run what failed. Returns the
 * unsubscribe function.
 */
export function subscribeSessionRefreshed(listener: () => void): () => void {
  sessionRefreshedListeners.add(listener);
  return () => {
    sessionRefreshedListeners.delete(listener);
  };
}

export function notifySessionRefreshed(): void {
  sessionRefreshedListeners.forEach((listener) => listener());
}
